import cx from 'classnames';
import Overlay from 'react-overlays/Overlay';
import { ConditionalView } from '../../utils';
import { WrapTextNodes } from '../../utils/WrapTextNodes';
import Icon from '../Icon';
import { PLACEMENT_TO_ALIGNMENT, PLACEMENT_TO_DIRECTION } from './Tooltip.constants';
import styles from './Tooltip.module.scss';
import usePresenter from './Tooltip.presenter';
import type { TooltipProps } from './Tooltip.types';

const Tooltip = (props: TooltipProps) => {
  const {
    direction,
    alignment,
    action,
    target,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    targetRef,
    cursorRef,
    updateRef,
    handleMouseMove,
    variantProps,
    style,
    visible,
    placement,
    arrowOffsetStyle,
  } = usePresenter(props);

  return (
    <div
      className={cx(styles.tooltipWrapper,
        {
          [styles.outlined]: style === 'Outlined'
        })}
      {...variantProps}
    >
      <div
        ref={targetRef}
        className={styles.tooltipTarget}
        onMouseEnter={action === 'Hover' || action === 'Follow' ? showTooltip : undefined}
        onMouseLeave={action === 'Hover' || action === 'Follow' ? hideTooltip : undefined}
        onMouseMove={action === 'Follow' ? handleMouseMove : undefined}
        onClick={action === 'Click' ? toggleTooltip : undefined}
      >
        {target}
      </div>
      <ConditionalView condition={action === 'Follow'}>
        <div
          ref={cursorRef}
          style={{
            position: 'fixed',
            width: 1,
            height: 1,
            pointerEvents: 'none',
          }}
        />
      </ConditionalView>
      <Overlay
        show={visible}
        target={action === 'Follow' ? cursorRef : targetRef}
        placement={placement}
        container={document.body}
        offset={[0, 10]}
        flip
      >
        {({ props: overlayProps, arrowProps, placement: actualPlacement, update }) => {
          updateRef.current = update;
          const actualPlacementParts = actualPlacement.split('-');
          const resolvedDirection = PLACEMENT_TO_DIRECTION[actualPlacementParts[0]] || direction;
          const resolvedAlignment = PLACEMENT_TO_ALIGNMENT[actualPlacementParts[1]] || alignment;

          return (
            <div
              className={cx(styles.tooltipContent, {
                [styles.outlined]: style === 'Outlined'
              }, props.className)}
              {...overlayProps}
              data-direction={resolvedDirection}
              data-align={resolvedAlignment}
              {...variantProps}
            >
              <WrapTextNodes className={styles.text}>
                {props.children}
              </WrapTextNodes>
              <div
                className={styles.tooltipArrow}
                ref={arrowProps.ref}
                style={arrowOffsetStyle} // sets the var(--arrow-offset) for the arrow position
              >
                <Icon className={cx(styles.tooltipArrowIcon, styles.tailFill)} icon="Tail" style="Filled" />
                <Icon className={cx(styles.tooltipArrowIcon, styles.tailOutline)} icon="Tail" style="Outlined" />
              </div>
            </div>
          );
        }}
      </Overlay>
    </div>
  );
};

export default Tooltip;
